import React, { useState, useEffect, useRef } from "react";
import { Input, useToast } from "@swordsweeper/ssi-ui-component-library";
import * as Yup from "yup";
import debounce from "lodash/debounce";
import * as UserSettingsApi from "@src/api/UserSettingsApi";
import SelectableManufacturerDisplay from "./SelectableManufacturerDisplay";
import SelectableProductDisplay from "./SelectableProductDisplay";
import SpinningLoader from "@src/components/common/SpinningLoader";

const MANUFACTURER = "manufacturer";
const PRODUCT = "product";

export const schema = Yup.object().shape({
  objectId: Yup.string().required(),
});

export default function AddHiddenItemForm({
  formData,
  onChange,
  isSuccessful,
}) {
  const { showToast } = useToast();
  const [search, setSearch] = useState(null);
  const [searchResults, setSearchResults] = useState(null);
  const [isLoadingItems, setIsLoadingItems] = useState(false);
  const [selectedItemContextData, setSelectedItemContextData] = useState(null);

  useEffect(() => {
    debouncedHandleSearch(search);
  }, [search]);

  const debouncedHandleSearch = useRef(
    debounce(async (search) => {
      if (!!search) {
        setIsLoadingItems(true);
        try {
          const response =
            await UserSettingsApi.searchHiddenItemOptions(search);
          setSearchResults(response.body);
        } catch (e) {
          console.error(e);
          showToast({
            message: "Error searching",
            variant: "error",
          });
        } finally {
          setIsLoadingItems(false);
        }
      } else {
        setSearchResults(null);
      }
    }, 500),
  ).current;

  const handleItemSelected = (item, type) => {
    const { id, ...contextData } = item;
    setSelectedItemContextData({
      ...contextData,
      type,
    });
    onChange({
      name: "objectId",
      value: id,
    });
  };

  const handleClearSelectedItem = () => {
    setSelectedItemContextData(null);
    onChange({
      name: "objectId",
      value: null,
    });
  };

  if (!!formData.objectId) {
    return (
      <>
        {isSuccessful && (
          <div>
            This item will no longer appear in search results or under any
            category listings.
          </div>
        )}
        {selectedItemContextData.type === PRODUCT && (
          <SelectableProductDisplay
            product={selectedItemContextData}
            onSelect={isSuccessful ? null : handleClearSelectedItem}
            data-testid={`add-hidden-item-form-product-option-${formData.objectId}`}
          />
        )}
        {selectedItemContextData.type === MANUFACTURER && (
          <SelectableManufacturerDisplay
            manufacturer={selectedItemContextData}
            onSelect={isSuccessful ? null : handleClearSelectedItem}
            data-testid={`add-hidden-item-form-manufacturer-option-${formData.objectId}`}
          />
        )}
      </>
    );
  }

  return (
    <>
      <div>Search by product or manufacturer.</div>
      <Input
        defaultValue=""
        onChange={setSearch}
        value={search}
        placeholder="Search..."
        width="100%"
        icon={<i className="fa-light fa-magnifying-glass" />}
        data-testid="add-hidden-item-form-search-input"
      />

      {!!searchResults?.products?.length > 0 && (
        <>
          <h4>Products</h4>
          <div className="spaced-content-v-xs">
            {searchResults.products.map((product, productIndex) => (
              <React.Fragment key={`product-option-${product.id}`}>
                <SelectableProductDisplay
                  product={product}
                  onSelect={() => handleItemSelected(product, PRODUCT)}
                  data-testid={`add-hidden-item-form-product-option-${product.id}`}
                />
                {productIndex < searchResults.products.length - 1 && <hr />}
              </React.Fragment>
            ))}
          </div>
        </>
      )}

      {!!searchResults?.manufacturers?.length > 0 && (
        <>
          <h4>Manufacturers</h4>
          <div className="spaced-content-v-xs">
            {searchResults.manufacturers.map(
              (manufacturer, manufacturerIndex) => (
                <React.Fragment key={`manufacturer-option-${manufacturer.id}`}>
                  <SelectableManufacturerDisplay
                    manufacturer={manufacturer}
                    onSelect={() =>
                      handleItemSelected(manufacturer, MANUFACTURER)
                    }
                    data-testid={`add-hidden-item-form-manufacturer-option-${manufacturer.id}`}
                  />
                  {manufacturerIndex <
                    searchResults.manufacturers.length - 1 && <hr />}
                </React.Fragment>
              ),
            )}
          </div>
        </>
      )}

      {isLoadingItems && <SpinningLoader />}
    </>
  );
}
